import axios from 'axios';
import Logger from '../utils/logger';

// In-memory token storage (not persisted, more secure than localStorage)
let authToken = null;
let refreshTokenValue = null;

// Create an axios instance with base configuration
// Using process.env.REACT_APP_API_URL to allow flexibility in API endpoint
// In development: REACT_APP_API_URL=/api (routed through setupProxy.js)
// In production: REACT_APP_API_URL=http://smartdash.api/api
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://smartdash.api/api',
  timeout: 15000, // 15 seconds timeout (increased from 10s)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Functions to set and get tokens
export const setAuthTokens = (accessToken, refreshToken) => {
  authToken = accessToken;
  refreshTokenValue = refreshToken;
};

export const getAuthToken = () => authToken;

export const clearAuthTokens = () => {
  authToken = null;
  refreshTokenValue = null;
};

// Function to refresh the token
const refreshToken = async () => {
  try {
    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://smartdash.api/api'}/auth/refresh`, {
      refreshToken: refreshTokenValue
    });

    if (response.data && response.data.accessToken) {
      authToken = response.data.accessToken;
      return response.data.accessToken;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error) {
    Logger.error('Token refresh failed', {
      error: error.message,
      timestamp: new Date().toISOString()
    });
    // Clear tokens and redirect to login
    clearAuthTokens();
    window.location.href = '/login';
    throw error;
  }
};

// Add a request interceptor to log requests
apiClient.interceptors.request.use(
  (config) => {
    // Get token from in-memory storage
    const token = authToken;

    // Exclude login endpoint from sending Authorization header
    if (token && !config.url.endsWith('/auth/login')) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set timeout for individual requests if not already set
    if (!config.timeout) {
      config.timeout = 15000; // Default timeout of 15 seconds
    }

    // Log the request
    Logger.info('API Request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      params: config.params,
      data: config.data,
      timestamp: new Date().toISOString()
    });

    return config;
  },
  (error) => {
    Logger.error('Request Error', {
      message: error.message,
      error: error,
      timestamp: new Date().toISOString()
    });
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle responses and errors
apiClient.interceptors.response.use(
  (response) => {
    // Log successful responses
    Logger.info('API Response', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      timestamp: new Date().toISOString()
    });

    return response;
  },
  async (error) => {
    // Log error responses (sensitive data will be sanitized by the logger)
    const errorData = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      // Note: We don't include error.response?.data here to avoid logging sensitive information
      timestamp: new Date().toISOString()
    };

    Logger.error('API Error', errorData);

    const originalRequest = error.config;

    // Handle token refresh when 401 or 403 error occurs (assuming JWT expiration)
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      // Mark the request as retry to prevent infinite loops
      originalRequest._retry = true;

      try {
        // Attempt to refresh the token
        const newToken = await refreshToken();

        // Update the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        Logger.warn('Token refresh failed, redirecting to login', {
          url: error.config.url,
          status: error.response?.status,
          message: 'Token refresh failed'
        });
        clearAuthTokens();
        window.location.href = '/login';
        throw error;
      }
    }
    // Handle other error responses here with generic messages for the user
    if (error.response?.status === 500) {
      // Log server errors specifically but don't expose details to the user
      Logger.error('Server Error', {
        url: error.config.url,
        status: 500,
        message: 'Internal server error'
      });
      // Return a generic error message to the user
      return Promise.reject(new Error('An internal server error occurred. Please try again later.'));
    } else if (error.response?.status === 404) {
      // Log not found errors
      Logger.warn('Resource Not Found', {
        url: error.config.url,
        status: 404,
        message: 'Requested resource not found'
      });
      // Return a generic error message to the user
      return Promise.reject(new Error('The requested resource was not found.'));
    }

    // For other errors, return a generic message
    if (error.response?.status) {
      return Promise.reject(new Error(`An error occurred (status: ${error.response.status}). Please try again.`));
    }

    return Promise.reject(error);
  }
);

export default apiClient;